/**
 * Anuncios con IA (1.0 AdminListingAI): completa anuncios sin descripción (o uno concreto por id)
 * con `admin-enrichListing` (categoría, descripción, marca, talla, color), uno a uno con
 * reintentos; interruptor `ai.listing_from_photos`.
 */
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { api } from '@nuvvu/shared/api';
import { call, errorText } from '../api.js';
import { db } from '../firebase.js';
import { btn, card, field, muted, Notice, str, Table, td } from '../lib/ui.js';

type Doc = Record<string, unknown>;
const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));
const needs = (p: Doc): boolean => str(p.description).trim().length <= 10;

export function ListingAiPage(): ReactElement {
  const [rows, setRows] = useState<Doc[] | null>(null);
  const [onlyPending, setOnlyPending] = useState(true);
  const [manualId, setManualId] = useState('');
  const [log, setLog] = useState<string[]>([]);
  const [running, setRunning] = useState(false);
  const stop = useRef<{ now: boolean }>({ now: false });
  const stopped = (): boolean => stop.current.now;
  const [error, setError] = useState<string | null>(null);
  const load = (): void => {
    getDocs(
      query(
        collection(db, 'products'),
        where('status', '==', 'active'),
        orderBy('created_date', 'desc'),
        limit(300),
      ),
    )
      .then((s) => {
        setRows(s.docs.map((d) => ({ id: d.id, ...d.data() })));
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      });
  };
  useEffect(load, []);
  const note = (m: string): void => {
    setLog((l) => [`${new Date().toLocaleTimeString('es')} ${m}`, ...l].slice(0, 200));
  };
  const enrich = async (id: string, skipIfDesc: boolean): Promise<void> => {
    for (let attempt = 0; attempt < 4; attempt++) {
      try {
        const r = await call(api.admin.enrichListing, {
          product_id: id,
          skip_if_description: skipIfDesc,
        });
        note(
          r.skipped
            ? `${id}: saltado (${r.reason ?? ''})`
            : `${id}: ${r.result?.category_id ?? '?'} · ${r.result?.size ?? ''} ${r.result?.color ?? ''}`,
        );
        return;
      } catch (e) {
        const msg = errorText(e);
        if (/resource-exhausted|unavailable|deadline/i.test(msg) && attempt < 3) {
          note(`${id}: ocupado, reintento en ${10 + attempt * 5} s`);
          await sleep((10 + attempt * 5) * 1000);
          continue;
        }
        note(`${id}: ERROR ${msg}`);
        return;
      }
    }
  };
  const runAll = async (): Promise<void> => {
    const pending = (rows ?? []).filter(needs);
    if (pending.length === 0) return;
    if (!window.confirm(`¿Procesar ${pending.length} anuncios con IA? (una llamada por anuncio)`))
      return;
    setRunning(true);
    stop.current.now = false;
    for (const p of pending) {
      if (stopped()) break;
      await enrich(str(p.id), true);
      await sleep(1500);
    }
    setRunning(false);
    load();
  };
  const shown = (rows ?? []).filter((p) => !onlyPending || needs(p));
  return (
    <>
      <h1 style={{ margin: 0 }}>Anuncios con IA</h1>
      <p style={muted}>
        Completa con IA (fotos → categoría, descripción, marca, talla, color) los anuncios activos
        sin descripción, de los 300 más recientes. Un anuncio concreto se procesa por id (y
        sobrescribe la descripción). Interruptor <code>ai.listing_from_photos</code>.
      </p>
      <Notice text={error} error />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          style={{ ...field, maxWidth: 300 }}
          placeholder="id de anuncio"
          value={manualId}
          onChange={(e) => {
            setManualId(e.target.value);
          }}
        />
        <button
          type="button"
          style={btn(false)}
          disabled={running || !manualId.trim()}
          onClick={() => {
            setRunning(true);
            void enrich(manualId.trim(), false).finally(() => {
              setRunning(false);
              load();
            });
          }}
        >
          Procesar este
        </button>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
          <input
            type="checkbox"
            checked={onlyPending}
            onChange={(e) => {
              setOnlyPending(e.target.checked);
            }}
          />
          Solo sin descripción
        </label>
        {!running ? (
          <button type="button" style={btn(true)} onClick={() => void runAll()}>
            Procesar todos los pendientes ({(rows ?? []).filter(needs).length})
          </button>
        ) : (
          <button
            type="button"
            style={btn(false, true)}
            onClick={() => {
              stop.current.now = true;
            }}
          >
            Parar
          </button>
        )}
      </div>
      {log.length > 0 && (
        <section
          style={{
            ...card,
            fontFamily: 'monospace',
            fontSize: 12,
            maxHeight: 220,
            overflow: 'auto',
          }}
        >
          {log.map((l, i) => (
            <div key={i}>{l}</div>
          ))}
        </section>
      )}
      {!rows && !error && <p style={muted}>Cargando…</p>}
      {rows && (
        <Table head={['Anuncio', 'Categoría', 'Descripción', 'Marca', 'Talla', '']}>
          {shown.slice(0, 200).map((p) => (
            <tr key={str(p.id)}>
              <td style={td}>
                <Link to={`/moderacion?id=${str(p.id)}`}>{str(p.title).slice(0, 60)}</Link>
              </td>
              <td style={td}>{str(p.category_id)}</td>
              <td style={td}>{str(p.description).slice(0, 60) || '—'}</td>
              <td style={td}>{str(p.brand) || '—'}</td>
              <td style={td}>{str(p.size) || '—'}</td>
              <td style={td}>
                <button
                  type="button"
                  style={btn(false)}
                  disabled={running}
                  onClick={() => {
                    setRunning(true);
                    void enrich(str(p.id), false).finally(() => {
                      setRunning(false);
                      load();
                    });
                  }}
                >
                  IA
                </button>
              </td>
            </tr>
          ))}
          {shown.length === 0 && (
            <tr>
              <td style={td} colSpan={6}>
                Nada pendiente.
              </td>
            </tr>
          )}
        </Table>
      )}
    </>
  );
}
